import { useState } from 'react';
import { Button, InlineNote, Sheet, sheetText, useNote, type Note } from '../components/ui';
import { useApp } from '../lib/app';
import { friendly } from '../lib/errors';
import { useT } from '../lib/i18n';
import { placeFor, TooManyPlaces, upsertPlace, type PlaceField } from '../lib/savedPlaces';
import type { Place } from '../lib/types';

/**
 * Saving a place (Ghar, Kaam or a named one) to the rider's account — PUT /v1/rider/me { places }.
 * A named place first asks for its name in a sheet. Returns true when saved.
 */
export function useSavePlace() {
  const { api, rider, setRider } = useApp();
  const [note, say] = useNote();
  const t = useT();
  const [asking, setAsking] = useState<{ place: Place; done: () => void } | null>(null);

  async function store(field: PlaceField, place: Place, name?: string): Promise<boolean> {
    try {
      const saved = placeFor(field, place, { home: t('home.home'), work: t('home.work') }, name);
      const r = await api.updateMe({ places: upsertPlace(rider?.places ?? [], saved) });
      setRider(r);
      return true;
    } catch (e) {
      say(e instanceof TooManyPlaces ? t('pickup.tooMany') : friendly(e, t), 'error');
      return false;
    }
  }

  /** Save now (Ghar/Kaam) or after the rider names it; `then` runs once saved. */
  function save(field: PlaceField, place: Place, then: () => void) {
    if (field !== 'place') return void store(field, place).then((ok) => ok && then());
    setAsking({ place, done: then });
  }

  const sheet = <NameSheet note={note} open={!!asking} onClose={() => setAsking(null)} onName={async (n) => (asking && (await store('place', asking.place, n)) ? (asking.done(), setAsking(null)) : undefined)} />;
  /** `note`: why a save failed, shown by the screen next to its button */
  return { save, sheet, note };
}

function NameSheet({ open, onClose, onName, note }: { open: boolean; onClose: () => void; onName: (n: string) => Promise<void>; note: Note }) {
  const t = useT();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <Sheet open={open} onClose={onClose} title={t('pickup.placeName')}>
      <p style={sheetText}>{t('places.max')}</p>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          if (!name.trim()) return;
          setBusy(true);
          await onName(name.trim());
          setBusy(false);
        }}
      >
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          aria-label={t('pickup.placeName')}
          placeholder={t('pickup.placeNameHint')}
          maxLength={40}
          className="zr-field"
          style={{ marginTop: 16, fontSize: 17, fontWeight: 700 }}
        />
        <InlineNote note={note} />
        <Button type="submit" busy={busy} disabled={!name.trim()} style={{ marginTop: 16 }}>
          {t('common.save')}
        </Button>
      </form>
    </Sheet>
  );
}
